import { IconProps } from '@tamagui/helpers-icon';
import { HelpCircle } from '@tamagui/lucide-icons';
import { Pressable } from '@unistocks/components/basic/pressable';
import { Row } from '@unistocks/components/layout/flex';
import { HeaderIcon } from '@unistocks/components/modal/header-icon';
import { Sheet } from '@unistocks/components/sheet/sheet';
import { Paragraph } from '@unistocks/components/text/text';
import { useBackHandler } from '@unistocks/shared/hooks/use-backhandler';
import { useCallback, useState } from 'react';
import { Adapt, Heading, Popover, useAdaptIsActive } from 'tamagui';

export type HelpTextProps = IconProps & {
  title?: string;
  text: string;
};

export const HelpText: React.FC<HelpTextProps> = ({ title, text, ...iconProps }) => {
  const [open, onOpenChange] = useState(false);

  useBackHandler(
    useCallback(() => {
      if (open) {
        onOpenChange(false);

        return true;
      }

      return false;
    }, [open, onOpenChange])
  );

  return (
    <Popover
      stayInFrame
      placement={'left-start'}
      size={'$true'}
      open={open}
      onOpenChange={onOpenChange}
    >
      <Popover.Trigger asChild testID="help-text-trigger">
        <Pressable
          withFeedback
          withScale
          role="button"
          accessibilityRole="button"
          tabIndex={0}
          accessible
          testID="help-text-trigger"
          hitSlop={12}
          ml={'auto'}
          focusVisibleStyle={{
            outlineStyle: 'solid',
            outlineWidth: 2,
            outlineColor: '$outlineColor'
          }}
        >
          <HelpCircle size={'$1'} color={open ? '$unistocks' : undefined} {...iconProps} />
        </Pressable>
      </Popover.Trigger>

      <Popover.Content
        elevation={'$1'}
        py={'$2'}
        px={'$3'}
        maxWidth={'$20'}
        borderWidth={1}
        borderColor={'$borderColor'}
        enterStyle={{ x: 0, y: 20, o: 0, scale: 0.9 }}
        exitStyle={{ x: 0, y: 20, o: 0, scale: 0.9 }}
        transition={'quickLessBouncy'}
      >
        <Popover.Arrow borderWidth={1} borderColor={'$borderColor'} />
        <HelpTextContent title={title} text={text} />
      </Popover.Content>

      <Adapt when={'max-sm'}>
        <Sheet
          contentTopMargin
          scrollMode="scrollView"
          snapPoints={['45%']}
          headerIcon={<HeaderIcon Icon={HelpCircle} />}
          headerTitle={title}
        >
          <Adapt.Contents />
        </Sheet>
      </Adapt>
    </Popover>
  );
};

HelpText.displayName = 'HelpText';

const HelpTextContent: React.FC<Pick<HelpTextProps, 'title' | 'text'>> = ({ title, text }) => {
  const isAdapted = useAdaptIsActive();

  return isAdapted ? (
    <Row>
      <Paragraph>{text}</Paragraph>
    </Row>
  ) : (
    <>
      <Row
        w={'100%'}
        pb={'$1'}
        alignItems="center"
        borderBottomWidth={1}
        borderColor={'$borderColor'}
      >
        <HeaderIcon Icon={HelpCircle} size={'$1'} />
        <Heading size={'$6'} flex={1}>
          {title}
        </Heading>
      </Row>
      <Paragraph mt={isAdapted ? undefined : '$2'}>{text}</Paragraph>
    </>
  );
};

HelpTextContent.displayName = 'HelpTextContent';
